import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { campaignService } from '../services/campaignService';
import type { SocialNetwork } from '@agency-smm/shared';

interface CampaignListItem {
  id: string;
  name: string;
  clientName: string | null;
  postsPerAccountPerWeek: number;
  platforms: SocialNetwork[];
  isActive: boolean;
  createdAt: string;
  workspaceCount: number;
  groupACount: number;
  groupBCount: number;
}

const ALL_PLATFORMS: { value: SocialNetwork; label: string }[] = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'gbp', label: 'Google Business' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'threads', label: 'Threads' },
  { value: 'bluesky', label: 'Bluesky' },
];

const GHL_DEFAULT_PLATFORMS: SocialNetwork[] = [
  'facebook', 'instagram', 'linkedin', 'twitter',
  'tiktok', 'gbp', 'youtube', 'pinterest', 'telegram',
];

// --- Styled Components ---

const PageWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const BetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #e0d0ff;
  background: linear-gradient(135deg, #7c3aed, #a855f7);
  border-radius: 999px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
`;

const Card = styled(Link)`
  display: block;
  text-decoration: none;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: 1.5rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.primary}44;
  }
`;

const CardName = styled.h3`
  font-size: 1.1rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 0.25rem;
`;

const CardClient = styled.p`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 1rem;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const MetaChip = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.border}33;
  padding: 0.2rem 0.55rem;
  border-radius: 6px;
`;

const StatusDot = styled.span<{ $active: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $active }) => ($active ? '#22c55e' : '#6b7280')};
  margin-right: 0.35rem;
`;

const StatusChip = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ $active }) => ($active ? '#22c55e' : '#6b7280')};
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  margin-left: auto;

  &:hover {
    color: #ef4444;
    background: #ef444418;
  }
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`;

// --- Create Form ---

const FormOverlay = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: 1.5rem;
`;

const FormTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 1rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FieldGroup = styled.div`
  flex: 1;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 0.35rem;
`;

const PlatformGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const PlatformCheckbox = styled.label<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: ${({ theme, $checked }) => ($checked ? theme.colors.text : theme.colors.textMuted)};
  padding: 0.3rem 0.6rem;
  border: 1px solid ${({ theme, $checked }) => ($checked ? theme.colors.primary : theme.colors.border)};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.12s ease;
  user-select: none;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  input {
    accent-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const LoadingText = styled.p`
  text-align: center;
  padding: 4rem 0;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const EmptyText = styled.p`
  text-align: center;
  padding: 4rem 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.95rem;
`;

const ErrorText = styled.p`
  text-align: center;
  padding: 2rem 0;
  color: #ef4444;
`;

// --- Component ---

export default function CampaignManagerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formClient, setFormClient] = useState('');
  const [formPlatforms, setFormPlatforms] = useState<SocialNetwork[]>([...GHL_DEFAULT_PLATFORMS]);
  const [creating, setCreating] = useState(false);

  // Guard: super_admin only
  useEffect(() => {
    if (user && (user as any).role !== 'super_admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await campaignService.list();
      setCampaigns(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const togglePlatform = (p: SocialNetwork) => {
    setFormPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  };

  const handleCreate = async () => {
    if (!formName.trim()) return;
    try {
      setCreating(true);
      await campaignService.create({
        name: formName.trim(),
        clientName: formClient.trim() || undefined,
        platforms: formPlatforms,
      });
      setFormName('');
      setFormClient('');
      setFormPlatforms([...GHL_DEFAULT_PLATFORMS]);
      setShowForm(false);
      await fetchCampaigns();
    } catch (err: any) {
      setError(err.message || 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this campaign? This cannot be undone.')) return;
    try {
      await campaignService.delete(id);
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete campaign');
    }
  };

  if (!user || (user as any).role !== 'super_admin') {
    return null;
  }

  return (
    <PageWrapper>
      <PageHeader>
        <TitleRow>
          <Title>Campaigns</Title>
          <BetaBadge>Beta</BetaBadge>
        </TitleRow>
        {!showForm && (
          <Button onClick={() => setShowForm(true)}>Create Campaign</Button>
        )}
      </PageHeader>

      {showForm && (
        <FormOverlay>
          <FormTitle>New Campaign</FormTitle>
          <FormRow>
            <FieldGroup>
              <Label>Campaign Name *</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Q2 Social Push"
                autoFocus
              />
            </FieldGroup>
            <FieldGroup>
              <Label>Client Name</Label>
              <Input
                value={formClient}
                onChange={(e) => setFormClient(e.target.value)}
                placeholder="Optional"
              />
            </FieldGroup>
          </FormRow>

          <Label>Platforms</Label>
          <PlatformGrid>
            {ALL_PLATFORMS.map((p) => {
              const checked = formPlatforms.includes(p.value);
              return (
                <PlatformCheckbox key={p.value} $checked={checked}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => togglePlatform(p.value)}
                  />
                  {p.label}
                </PlatformCheckbox>
              );
            })}
          </PlatformGrid>

          <FormActions>
            <Button
              $variant="secondary"
              onClick={() => {
                setShowForm(false);
                setFormName('');
                setFormClient('');
                setFormPlatforms([...GHL_DEFAULT_PLATFORMS]);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating || !formName.trim()}>
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </FormActions>
        </FormOverlay>
      )}

      {error && <ErrorText>{error}</ErrorText>}

      {loading ? (
        <LoadingText>Loading campaigns...</LoadingText>
      ) : campaigns.length === 0 ? (
        <EmptyText>No campaigns yet. Create one to get started.</EmptyText>
      ) : (
        <CardGrid>
          {campaigns.map((c) => (
            <Card key={c.id} to={`/campaigns/${c.id}`}>
              <CardTopRow>
                <div>
                  <CardName>{c.name}</CardName>
                  <CardClient>{c.clientName || 'No client'}</CardClient>
                </div>
                <DeleteButton onClick={(e) => handleDelete(e, c.id)} title="Delete campaign">
                  Delete
                </DeleteButton>
              </CardTopRow>
              <CardMeta>
                <StatusChip $active={c.isActive}>
                  <StatusDot $active={c.isActive} />
                  {c.isActive ? 'Active' : 'Inactive'}
                </StatusChip>
                <MetaChip>
                  {c.workspaceCount} workspace{c.workspaceCount !== 1 ? 's' : ''}
                </MetaChip>
                <MetaChip>A: {c.groupACount} / B: {c.groupBCount}</MetaChip>
                <MetaChip>{c.platforms.length} platform{c.platforms.length !== 1 ? 's' : ''}</MetaChip>
              </CardMeta>
            </Card>
          ))}
        </CardGrid>
      )}
    </PageWrapper>
  );
}
